import { Container, Graphics } from "pixi.js";
import { Lane } from "../../track/lane";
import { LocalTransport } from "../../track/local-transport";
import { readHandPalette, type HandPalette } from "@/lib/studio/theme/hand-palette";
import type { ColorToken } from "@/lib/studio/lane-view";
import { EMPTY_LAYOUT, type SequenceLayout } from "@/lib/studio/view/sequence-layout";

/** Resolves the color token for a lane (origin-based mapping). */
export type ColorTokenResolver = (lane: Lane) => ColorToken;

/** Fallback cycle for orchestral parts (default token, beyond LH/RH). */
const EXTRA_TRACK_HEX = [0xf59e0b, 0x4ade80, 0x60a5fa, 0xf472b6];

/**
 * Une frontière entre deux morceaux, en travers du rail. Plus discrète que
 * la ligne de position (0,9), plus nette que le bord du rail (0,06) : un
 * repère qu'on voit sans le chercher, sous lequel les notes restent lisibles.
 */
const BOUNDARY_ALPHA = 0.35;

/** Une lane posée sur l'axe global du rail, à son décalage. */
type LaidOutLane = {
  lane: Lane;
  laneIdx: number;
  /** Où tombe son tick 0 sur l'axe global, en ticks. */
  offset: number;
};

/**
 * Configuration for the minimap renderer.
 */
export type MinimapConfig = {
  /** Width of the minimap strip in pixels */
  width: number;
  /** Height of the minimap (matches piano + piano roll height) */
  height: number;
  /** Number of horizontal columns (MIDI buckets) for note aggregation */
  columns: number;
  /** Target number of vertical rows (time buckets) for note aggregation */
  rows: number;
};

const DEFAULT_CONFIG: MinimapConfig = {
  width: 60,
  height: 400,
  columns: 12,
  rows: 0, // computed dynamically in computeRows()
};

/**
 * Pixi.js renderer for the minimap, a condensed overview of the entire song.
 *
 * Architecture:
 * - Reads all notes from Lane[] (same data as PianoRollRenderer)
 * - Gets the current tick from LocalTransport
 * - Maps the full song duration vertically (bottom = start, top = end),
 *   dans le même sens que le piano roll où les notes tombent vers le clavier
 * - Maps MIDI note range horizontally (compressed)
 * - Shows current position as a horizontal line
 * - Shows the piano roll visible window as a semi-transparent rectangle
 *
 * Instead of drawing individual notes (too small to be visible), nearby notes
 * are aggregated into a grid of buckets (columns × rows). Each bucket that
 * contains at least one note is drawn as a rounded rectangle, with opacity
 * proportional to the note density. This produces a readable "heatmap" overview.
 *
 * The minimap is NOT rebuilt every frame, only the position indicators move.
 * Notes are rebuilt when lanes change (via rebuildNotes()).
 */
export class MinimapRenderer {
  private config: MinimapConfig;
  private background: Graphics;
  private noteGraphics: Graphics;
  /**
   * Une grille par lane, réutilisée d'une reconstruction à l'autre : la
   * reconstruction allouait `rows` tableaux par lane (près de 200 sur un
   * canvas haut), et elle tourne à chaque note tenue.
   */
  private readonly gridBuffers: Int32Array[] = [];
  private positionLine: Graphics;
  private viewportRect: Graphics;
  /** Les frontières de la séquence, redessinées avec les notes. */
  private boundaryGraphics: Graphics;
  private lanes: Lane[] = [];

  /** Cached song extent (recomputed when lanes change) */
  private songMinTick = 0;
  private songMaxTick = 0;
  private noteMinMidi = 21; // A0
  private noteMaxMidi = 108; // C8

  /** The visible tick range of the piano roll (set by the component) */
  private visibleTickRange = 960;

  /**
   * Des notes attendent encore la tête de lecture, masquées par `visibleUntil`.
   * Rien ne signale le moment où la tête les atteint : tant qu'il y en a, la
   * minimap doit se reconstruire d'elle-même, sinon les dernières notes d'un
   * récital n'y apparaîtraient jamais.
   */
  private hasNotesAheadOfHead = false;

  get notesAheadOfHead(): boolean {
    return this.hasNotesAheadOfHead;
  }

  /** Last rendered tick, skip update() when the transport hasn't moved */
  private previousTick = NaN;

  /** Hand-color palette (read once from CSS at construction). */
  private palette: HandPalette;

  /** Maps a lane to its color token (origin-based). */
  private getColorToken: ColorTokenResolver;

  /** Counter to assign extra-track colors to lanes whose token is `default`. */
  private extraTrackColorByLane = new Map<Lane, number>();

  /**
   * `sequence` est la séquence de morceaux vue depuis le rouleau. Le rail,
   * lui, dessine sur l'axe **global** — le début de la séquence à zéro, les
   * durées accumulées — pour qu'un changement de morceau ne le redessine
   * pas : seule la position, lue en ticks du morceau courant, est ramenée
   * sur cet axe par `currentOffset`. Sans séquence, ce décalage est nul et
   * les deux axes sont le même.
   */
  constructor(
    private stage: Container,
    private localTransport: LocalTransport,
    getColorToken: ColorTokenResolver,
    config?: Partial<MinimapConfig>,
    private sequence: SequenceLayout = EMPTY_LAYOUT,
  ) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.getColorToken = getColorToken;
    this.palette = readHandPalette();

    this.background = new Graphics();
    this.noteGraphics = new Graphics();
    this.boundaryGraphics = new Graphics();
    this.positionLine = new Graphics();
    this.viewportRect = new Graphics();
  }

  /**
   * Resolve a lane's drawing color (hex). Hand-tagged lanes use the palette;
   * orchestral parts (default token) get a stable cycle of extra colors.
   */
  private resolveLaneHex(lane: Lane): number {
    const token = this.getColorToken(lane);

    if (token !== 'default') {
      return this.palette[token].hex;
    }

    let extraIdx = this.extraTrackColorByLane.get(lane);

    if (extraIdx === undefined) {
      extraIdx = this.extraTrackColorByLane.size % EXTRA_TRACK_HEX.length;
      this.extraTrackColorByLane.set(lane, extraIdx);
    }

    return EXTRA_TRACK_HEX[extraIdx];
  }

  /**
   * Build the initial scene.
   * Call this once after construction.
   */
  build(): void {
    this.drawBackground();
    this.stage.addChild(this.background);
    this.stage.addChild(this.noteGraphics);
    this.stage.addChild(this.boundaryGraphics);
    this.stage.addChild(this.viewportRect);
    this.stage.addChild(this.positionLine);
  }

  /**
   * Set the lanes to display. Call this whenever tracks change.
   * This triggers a full rebuild of the note graphics.
   */
  setLanes(lanes: Lane[]): void {
    this.lanes = lanes;
    // Drop stale extra-color assignments for lanes no longer present.
    const present = new Set(lanes);
    for (const lane of [...this.extraTrackColorByLane.keys()]) {
      if (!present.has(lane)) {
        this.extraTrackColorByLane.delete(lane);
      }
    }
    this.rebuildNotes();
  }

  /**
   * Set the visible tick range of the piano roll (for the viewport rectangle).
   */
  setVisibleTickRange(range: number): void {
    this.visibleTickRange = range;
  }

  /**
   * Called every frame. Only updates the position indicators (cheap).
   * Skips entirely when the transport tick hasn't changed (e.g. paused).
   * @param effectiveTick, if provided, use this instead of localTransport.currentTick()
   */
  update(effectiveTick?: number): void {
    if (this.songMaxTick <= this.songMinTick) return;

    // La position arrive en ticks du morceau courant ; le rail est sur l'axe global.
    const currentTick = (effectiveTick ?? this.localTransport.currentTick()) + this.sequence.currentOffset;

    // Skip redraw when the playhead hasn't moved since last frame
    if (currentTick === this.previousTick) return;
    this.previousTick = currentTick;
    const { width, height } = this.config;

    // --- Position line (current playback position) ---
    // Bas = début du morceau, haut = fin. Même sens que le piano roll, où
    // les notes descendent vers le clavier : la lecture monte dans le rail.
    const clampedPosY = Math.max(0, Math.min(height, this.tickToY(currentTick)));

    this.positionLine.clear();
    this.positionLine.moveTo(0, clampedPosY);
    this.positionLine.lineTo(width, clampedPosY);
    this.positionLine.stroke({ color: 0xffffff, alpha: 0.9, width: 1.5 });

    // --- Viewport rectangle (what the piano roll currently shows) ---
    // La fenêtre s'étend vers l'avenir, donc vers le haut : le tick courant
    // en borne le bas, le tick le plus lointain le haut.
    const vpTop = Math.max(0, Math.min(height, this.tickToY(currentTick + this.visibleTickRange)));
    const vpBottom = Math.max(0, Math.min(height, this.tickToY(currentTick)));
    const vpHeight = Math.max(2, vpBottom - vpTop);

    this.viewportRect.clear();
    this.viewportRect.rect(0, vpTop, width, vpHeight);
    this.viewportRect.fill({ color: 0xffffff, alpha: 0.08 });
    this.viewportRect.stroke({ color: 0xffffff, alpha: 0.25, width: 1 });
  }

  /**
   * Position verticale d'un tick dans le rail. Axe inversé : le début du
   * morceau est en bas, la fin en haut, comme le piano roll.
   */
  private tickToY(tick: number): number {
    const { height } = this.config;
    const songDuration = this.songMaxTick - this.songMinTick;

    if (songDuration <= 0) {
      return height;
    }

    return height - ((tick - this.songMinTick) / songDuration) * height;
  }

  /**
   * Compute the number of vertical rows so that cells stay roughly square.
   * cellW = usableWidth / columns, so we pick rows = usableHeight / cellW.
   * Clamped to a reasonable range [40, 400].
   */
  private computeRows(): number {
    const { width, height, columns } = this.config;
    const padX = 3;
    const padY = 1;
    const usableWidth = width - padX * 2;
    const usableHeight = height - padY * 2;
    const cellW = usableWidth / columns;
    if (cellW <= 0) return 120;
    return Math.max(40, Math.min(400, Math.round(usableHeight / cellW)));
  }

  /**
   * Rebuild all note graphics using bucket aggregation.
   * Notes are grouped into a grid of (columns × rows) cells.
   * Each occupied cell is drawn as a single rectangle with opacity
   * proportional to note density. This reduces visual noise and
   * produces a readable "heatmap" overview.
   *
   * @param currentTick, if provided, held notes (no tickDuration yet) extend
   *   visually to this tick instead of being treated as zero-length.
   */
  rebuildNotes(currentTick?: number): void {
    this.noteGraphics.clear();
    this.hasNotesAheadOfHead = false;

    // Compute song extent across all lanes
    this.computeSongExtent(currentTick);

    if (this.songMaxTick <= this.songMinTick) return;

    // Recompute rows to keep cells roughly square at current height
    this.config.rows = this.computeRows();

    const { width, height, columns, rows } = this.config;
    const songDuration = this.songMaxTick - this.songMinTick;
    const midiRange = this.noteMaxMidi - this.noteMinMidi;
    if (midiRange <= 0) return;

    const padX = 3;
    const padY = 1;
    const usableWidth = width - padX * 2;
    const usableHeight = height - padY * 2;
    const cellW = usableWidth / columns;
    const cellH = usableHeight / rows;
    const gap = 1; // gap between cells

    // Per-lane bucket grids: grid[row * columns + col] = note count
    type LaneBuckets = { grid: Int32Array; maxCount: number; lane: Lane };
    const laneBuckets: LaneBuckets[] = [];
    const cells = rows * columns;

    for (const { lane, laneIdx, offset } of this.laidOutLanes()) {
      if (lane.isHidden()) continue;

      let grid = this.gridBuffers[laneIdx];

      if (grid === undefined || grid.length !== cells) {
        grid = new Int32Array(cells);
        this.gridBuffers[laneIdx] = grid;
      } else {
        grid.fill(0);
      }

      // La fenêtre et la limite se lisent dans les ticks de la lane, les
      // rangées se comptent sur l'axe global : le décalage entre au passage.
      const notes = lane.getNotesInRange(0, this.songMaxTick - offset);
      const until = this.visibleUntil(lane, currentTick);
      let maxCount = 0;

      for (const note of notes) {
        if (note.tick > until) {
          continue;
        }

        const midi = note.note.toMidi();

        // Column: map MIDI to bucket
        const col = Math.floor(((midi - this.noteMinMidi) / midiRange) * (columns - 1));
        const clampedCol = Math.max(0, Math.min(columns - 1, col));

        // Row(s): a note can span multiple time buckets.
        // Held notes (no tickDuration yet) extend to currentTick.
        const tickStart = note.tick;
        const tickEnd = Math.min(
          until,
          note.tickDuration !== undefined ? tickStart + note.tickDuration : (currentTick ?? tickStart),
        );

        const rowStart = Math.floor(((tickStart + offset - this.songMinTick) / songDuration) * rows);
        const rowEnd = Math.floor(((tickEnd + offset - this.songMinTick) / songDuration) * rows);

        const rStart = Math.max(0, Math.min(rows - 1, rowStart));
        const rEnd = Math.max(0, Math.min(rows - 1, rowEnd));

        for (let r = rStart; r <= rEnd; r++) {
          const cell = r * columns + clampedCol;
          grid[cell]++;
          if (grid[cell] > maxCount) {
            maxCount = grid[cell];
          }
        }
      }

      if (maxCount > 0) {
        laneBuckets.push({ grid, maxCount, lane });
      }
    }

    // Draw each lane's buckets
    for (const { grid, maxCount, lane } of laneBuckets) {
      const color = this.resolveLaneHex(lane);

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < columns; c++) {
          const count = grid[r * columns + c];
          if (count === 0) continue;

          // Alpha: scale from 0.3 (single note) to 0.9 (max density)
          const density = count / maxCount;
          const alpha = 0.3 + density * 0.6;

          // Ligne 0 = début du morceau, dessinée tout en bas : l'axe des
          // temps est inversé pour suivre le piano roll.
          const x = padX + c * cellW + gap / 2;
          const y = padY + (rows - 1 - r) * cellH + gap / 2;
          const w = cellW - gap;
          const h = cellH - gap;

          this.noteGraphics.roundRect(x, y, Math.max(1, w), Math.max(1, h), 1.5);
          this.noteGraphics.fill({ color, alpha });
        }
      }
    }

    this.drawBoundaries();
  }

  /**
   * Les frontières de la séquence en travers du rail, sur l'axe global.
   *
   * Refaites avec les notes et non à chaque image : elles ne bougent que si
   * l'étendue du rail bouge, et l'étendue ne bouge qu'à une reconstruction.
   * Rien sans séquence — la liste est vide, et le calque avec elle.
   */
  private drawBoundaries(): void {
    this.boundaryGraphics.clear();

    const boundaries = this.sequence.boundaries();

    if (boundaries.length === 0) {
      return;
    }

    const { width, height } = this.config;
    const currentOffset = this.sequence.currentOffset;

    for (const boundary of boundaries) {
      const y = this.tickToY(boundary.tick + currentOffset);

      if (y < 0 || y > height) {
        continue;
      }

      this.boundaryGraphics.moveTo(0, y);
      this.boundaryGraphics.lineTo(width, y);
    }

    this.boundaryGraphics.stroke({ color: 0xffffff, alpha: BOUNDARY_ALPHA, width: 1 });
  }

  /**
   * Toutes les lanes à dessiner, à leur place sur l'axe global : celles en
   * scène au décalage du morceau courant, celles des morceaux hors scène au
   * leur, indexées à la suite pour que chacune garde sa grille de cases.
   * Sans séquence, ce sont les lanes en scène à zéro, et rien d'autre.
   */
  private laidOutLanes(): LaidOutLane[] {
    const currentOffset = this.sequence.currentOffset;
    const laidOut: LaidOutLane[] = [];

    for (let laneIdx = 0; laneIdx < this.lanes.length; laneIdx++) {
      laidOut.push({ lane: this.lanes[laneIdx], laneIdx, offset: currentOffset });
    }

    for (const song of this.sequence.offstage()) {
      for (const lane of song.lanes) {
        laidOut.push({ lane, laneIdx: laidOut.length, offset: currentOffset + song.offsetTicks });
      }
    }

    return laidOut;
  }

  /**
   * Compute the min/max tick and MIDI range across all lanes.
   * @param currentTick, held notes (no tickDuration) extend to this tick.
   */
  /**
   * Jusqu'où une lane se montre sur la minimap.
   *
   * Une lane datée sur le transport partagé — mon jeu en séance, celui d'un
   * partenaire — ne se montre que jusqu'à la tête de lecture. Ses notes sont
   * captées au présent de la séance, et la tête court derrière lui du délai de
   * restitution : quatre secondes chez le spectateur d'un récital. La minimap
   * les dessinait donc en avance, ce que la tête n'a pas encore atteint — le
   * futur. Le roll, lui, les montre au-dessus de la tête, et c'est voulu : elles
   * tombent. La minimap résume ce qui a été joué.
   *
   * Un morceau chargé se montre en entier : c'est une partition, son futur est
   * écrit.
   */
  private visibleUntil(lane: Lane, currentTick?: number): number {
    if (!lane.hasSharedTimebase) {
      return Infinity;
    }

    return currentTick ?? this.localTransport.currentTick();
  }

  private computeSongExtent(currentTick?: number): void {
    let minTick = Infinity;
    let maxTick = 0;
    let minMidi = 127;
    let maxMidi = 0;
    let hasNotes = false;

    for (const { lane, offset } of this.laidOutLanes()) {
      // Use lane's note range for MIDI
      const range = lane.getNoteRange();
      if (range) {
        hasNotes = true;
        if (range.min < minMidi) minMidi = range.min;
        if (range.max > maxMidi) maxMidi = range.max;
      }

      // Scan notes for tick range
      const notes = lane.getNotesInRange(0, Infinity);
      const until = this.visibleUntil(lane, currentTick);

      for (const n of notes) {
        if (n.tick > until) {
          this.hasNotesAheadOfHead = true;
          continue;
        }

        if (n.tick + offset < minTick) {
          minTick = n.tick + offset;
        }

        // Held notes extend to currentTick
        const end = Math.min(
          until,
          n.tickDuration !== undefined ? n.tick + n.tickDuration : (currentTick ?? n.tick),
        ) + offset;
        if (end > maxTick) maxTick = end;
      }
    }

    if (hasNotes) {
      this.songMinTick = Math.max(0, minTick);
      this.songMaxTick = maxTick;
      // Add a small margin on MIDI range
      this.noteMinMidi = Math.max(0, minMidi - 2);
      this.noteMaxMidi = Math.min(127, maxMidi + 2);
    } else {
      this.songMinTick = 0;
      this.songMaxTick = 0;
    }
  }

  /**
   * Convert a Y position (in local coords) to a tick value.
   * Used for click-to-seek.
   */
  yToTick(y: number): number {
    const { height } = this.config;
    const songDuration = this.songMaxTick - this.songMinTick;
    if (songDuration <= 0) return 0;

    // Axe inversé : le haut du rail est la fin du morceau.
    const ratio = Math.max(0, Math.min(1, 1 - y / height));
    return this.songMinTick + ratio * songDuration;
  }

  /**
   * Returns whether the minimap has content to display.
   */
  hasContent(): boolean {
    return this.songMaxTick > this.songMinTick;
  }

  /**
   * Draw the dark background.
   */
  private drawBackground(): void {
    const { width, height } = this.config;
    this.background.clear();
    this.background.rect(0, 0, width, height);
    this.background.fill({ color: 0x0c0c18 });

    // Subtle left border
    this.background.moveTo(0, 0);
    this.background.lineTo(0, height);
    this.background.stroke({ color: 0xffffff, alpha: 0.06, width: 1 });
  }

  /**
   * Rebuild background (e.g., after resize).
   *
   * No-ops when the dimensions are unchanged: `rebuildNotes()` re-creates
   * every note graphic, so calling this on a width-only studio resize (e.g.
   * collapsing the sidebar, where the minimap keeps its fixed width and
   * height) would needlessly rebuild the whole minimap and stutter the frame.
   */
  resize(width: number, height: number): void {
    if (this.config.width === width && this.config.height === height) {
      return;
    }

    this.config.width = width;
    this.config.height = height;
    this.drawBackground();
    this.rebuildNotes();
  }

  /** Width of the minimap in pixels */
  getWidth(): number {
    return this.config.width;
  }

  /** Height of the minimap in pixels */
  getHeight(): number {
    return this.config.height;
  }

  /**
   * Cleanup all Pixi objects.
   */
  destroy(): void {
    this.lanes = [];
    this.positionLine.destroy();
    this.viewportRect.destroy();
    this.boundaryGraphics.destroy();
    this.noteGraphics.destroy();
    this.background.destroy();
  }
}
